import { CircularProgress } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import { Autocomplete } from "@material-ui/lab";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import { RotaryErrorFragment } from "@saleor/fragments/types/RotaryErrorFragment";
import useModalDialogErrors from "@saleor/hooks/useModalDialogErrors";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { getRotaryItemErrorMessage } from "@saleor/rotary/error";
import { useAllVouchersQuery } from "@saleor/rotary/queries";
import {
  RotaryDetails_rotary_items,
  RotaryDetails_rotary_items_voucher
} from "@saleor/rotary/types/RotaryDetails";
import { getFormErrors } from "@saleor/utils/errors";
import getRotaryErrorMessage from "@saleor/utils/errors/rotary";
import React, { Dispatch, SetStateAction } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface RotaryItemEditDialogFormData {
  text: string;
  voucher: RotaryDetails_rotary_items_voucher;
  probability: number;
}
export interface RotaryItemEditDialogProps {
  errors: RotaryErrorFragment[];
  rotaryItem: RotaryDetails_rotary_items | null;
  confirmButtonState: ConfirmButtonTransitionState;
  disabled: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RotaryItemEditDialogFormData) => void;
  textChange: Dispatch<SetStateAction<boolean>>;
}

const RotaryItemEditDialog: React.FC<RotaryItemEditDialogProps> = ({
  rotaryItem,
  confirmButtonState,
  disabled,
  errors: apiErrors,
  onClose,
  onSubmit,
  open,
  textChange
}) => {
  const intl = useIntl();
  const errors = useModalDialogErrors(apiErrors, open);
  const formErrors = getFormErrors(["text", "probability"], errors);
  const initialForm: RotaryItemEditDialogFormData = {
    probability: maybe(() => rotaryItem.probability, 0),
    text: maybe(() => rotaryItem.text, ""),
    voucher: maybe(() => rotaryItem.voucher, null)
  };

  const data1 = useAllVouchersQuery({
    variables: null
  });

  const options = data1 ? data1.data?.vouchersToNoShopAll : [];

  const loading = open && options?.length === 0;

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        {rotaryItem === null ? (
          <FormattedMessage
            defaultMessage="Add Item"
            description="add Rotary Item"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Edit Item"
            description="edit Rotary Item"
          />
        )}
      </DialogTitle>
      <Form initial={initialForm} onSubmit={onSubmit}>
        {({ change, data, submit }) => (
          <>
            <DialogContent>
              <TextField
                autoFocus
                disabled={disabled}
                fullWidth
                name={"text" as keyof RotaryItemEditDialogFormData}
                label={intl.formatMessage({
                  defaultMessage: "Text",
                  description: "Text"
                })}
                value={data.text}
                onChange={event => {
                  change(event);
                  textChange(true);
                }}
                error={!!formErrors.text}
                helperText={getRotaryItemErrorMessage(formErrors.text, intl)}
              />
              <FormSpacer />
              <TextField
                autoFocus
                disabled={disabled}
                fullWidth
                name={"probability" as keyof RotaryItemEditDialogFormData}
                label={intl.formatMessage({
                  defaultMessage: "Probability",
                  description: "Probability"
                })}
                value={data.probability}
                onChange={event => {
                  change(event);
                  // textChange(true);
                }}
                error={!!formErrors.probability}
                helperText={getRotaryErrorMessage(formErrors.probability, intl)}
              />
              <FormSpacer />
              <Autocomplete
                id={"voucher " as keyof RotaryItemEditDialogFormData}
                defaultValue={data.voucher}
                getOptionSelected={(option, value) => option.id === value.id}
                getOptionLabel={option => option.code}
                options={options}
                loading={loading}
                onChange={(_event, newValue) =>
                  (data.voucher = newValue as RotaryDetails_rotary_items_voucher)
                }
                renderInput={params => (
                  <>
                    <TextField
                      {...params}
                      {...data}
                      value={data.voucher}
                      variant="outlined"
                      name={"voucher" as keyof RotaryItemEditDialogFormData}
                      label="Vouchers"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loading ? (
                              <CircularProgress color="inherit" />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                      onChange={change}
                    />
                  </>
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  onClose();
                  textChange(false);
                }}
              >
                <FormattedMessage {...buttonMessages.back} />
              </Button>
              <ConfirmButton
                transitionState={confirmButtonState}
                color="primary"
                variant="contained"
                onClick={submit}
              >
                <FormattedMessage {...buttonMessages.save} />
              </ConfirmButton>
            </DialogActions>
          </>
        )}
      </Form>
    </Dialog>
  );
};
export default RotaryItemEditDialog;
